import Button from "@/components/atoms/Button";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import { Account } from "@/types/Account";
import { Switch } from "@/components/atoms/Switch";
import Input from "@/components/atoms/Input";
import {
  Select,
  SelectTrigger,
  SelectItem,
  SelectContent,
  SelectValue,
} from "@/components/atoms/Select";
import * as Constants from "@/constants";
import AccountSchema from "@/schemas/account";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { CheckboxButton } from "@/components/atoms/Checkbox";
import ThresholdList, { Threshold } from "@/components/molecules/ThresholdList";
import Link from "@/components/atoms/Link";
import { capitalize } from "@/lib/utils";
import ErrorType from "@/types/ErrorType";
import useAccess from "@/hooks/useAccess";

type Props = {
  mode?: "create" | "edit";
  account: Account | null;
  edit: boolean;
  onSubmit: (data: any) => void;
  errors: ErrorType;
};

const AccountForm = ({ mode = "edit", edit, onSubmit, account, errors }: Props) => {
  const { getAccess } = useAccess();
  const [thresholds, setThresholds] = useState<Threshold[]>(
    account?.status_inactivity_threshold?.map((threshold) => ({
      status: threshold.status,
      threshold: parseInt(threshold.threshold || "0"),
    })) || []
  );
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const form = useForm({
    resolver: zodResolver(AccountSchema),
    defaultValues: account
      ? {
          ...account,
          status: account?.status || Constants.ACCOUNT_STATUSES.ACTIVE,
          avatar: account.avatar || null,
        }
      : {
          azure_devops_integration: false,
          currency: "",
          external_quality_score: false,
          id: null,
          initials: "",
          crm_id: null,
          is_budget_visible: false,
          name: "",
          rate_us: false,
          rating_show_in_ticket: false,
          sender_email: "",
          status: Constants.ACCOUNT_STATUSES.ACTIVE,
          ticket_inactivity_notification: false,
          avatar: null
        },

  });

  const handleSubmit = (data: any) => {
    const updatedData = {
      ...data,
      status_inactivity_threshold: thresholds,
      ...(avatarFile ? { avatar: avatarFile } : {}),
    };
  
    if (!avatarFile) {
      delete updatedData.avatar;
    }
  
    onSubmit(updatedData);
  };
  

  

  useEffect(() => {
    if (!edit) {
      form.reset({ ...account });
      setThresholds(
        account?.status_inactivity_threshold?.map((threshold) => ({
          status: threshold.status,
          threshold: parseInt(threshold.threshold || "0"),
        })) || []
      );
    }
  }, [edit]);

  useEffect(() => {
    if (errors) {
      Object.entries(errors).forEach(([key, messages]) => {
        form.setError(key as keyof typeof form.getValues, {
          type: "manual",
          message: `${capitalize(key)} ${messages[0]}`,
        });
      });
    }
  }
  , [errors]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const maxSizeInBytes = 1 * 1024 * 1024;
    const allowedImageTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

    if (event.target.files && event.target.files[0]) {
      if (event.target.files[0].size > maxSizeInBytes) {
        alert('File size exceeds the 1MB limit. Please select a smaller file.');
        event.target.value = '';
      } else if(!allowedImageTypes.includes(event.target.files[0].type)) {
        alert("Only image files (JPEG, PNG, GIF, WebP) are allowed. Please select a valid image.");
        event.target.value = '';
      } else {
        setAvatarFile(event.target.files[0]);
      }
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="w-1/2">
              <FormLabel>Name*</FormLabel>
              <FormControl>
                <Input {...field} disabled={!edit} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="initials"
          render={({ field }) => (
            <FormItem className="w-1/2">
              <FormLabel>Initials*</FormLabel>
              <FormControl>
                <Input {...field} disabled={!edit} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="sender_email"
          render={({ field }) => (
            <FormItem className="w-1/2">
              <FormLabel>Sender Email*</FormLabel>
              <FormControl>
                <Input
                  placeholder="account@nmqdigital.com"
                  danger={!!form.formState.errors[field.name]}
                  {...field}
                  type="email"
                  disabled={!edit}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
 
        <FormField
          control={form.control}
          name="crm_id"
          render={({ field }) => (
            <FormItem className="w-1/2">
              <FormLabel>CRM ID</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value || undefined}
                  disabled={getAccess(["edit_crm_id"]) ? !edit : true}
                  type="number"
                  onChange={(e) =>
                    field.onChange(parseInt(e.currentTarget.value))
                  }
                  min={0}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
     
        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem className="w-1/2">
              <FormLabel>Currency*</FormLabel>
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={!edit}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {Constants.CURRENCIES.map((currency) => (
                      <SelectItem key={currency} value={currency}>
                        {currency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!edit && account?.avatar && (
          <div className="w-24 h-24 rounded-full overflow-hidden">
            <img
              src={typeof account.avatar === "string" ? account.avatar : URL.createObjectURL(account.avatar)}
              alt="Avatar"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        {!edit && !account?.avatar && <p>No avatar uploaded</p>}
        {edit && (
          <FormField
            control={form.control}
            name="avatar"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel>Avatar</FormLabel>
                <FormControl>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={!edit}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}



        <FormField
          control={form.control}
          name="flexi_thresholds"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Flexi Sow Thresholds %</FormLabel>
              <FormControl>
                <div className="space-y-2">
                  {field.value?.map((threshold: number, index: number) => (
                    <div key={index} className="flex items-center space-x-2 w-44">
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        step="1"
                        value={threshold}
                        onChange={(e) => {
                          const newThresholds = [...field.value];
                          newThresholds[index] = parseInt(e.target.value, 10);
                          field.onChange(newThresholds);
                        }}
                        disabled={!edit}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const newThresholds = field.value.filter((_, i) => i !== index);
                          field.onChange(newThresholds);
                        }}
                        disabled={!edit}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => field.onChange([...(field.value || []), 0])}
                    disabled={!edit}
                  >
                    Add Threshold
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="py-2">
          <div className="flex flex-wrap gap-3 mt-2">
            <FormField
              control={form.control}
              name="is_budget_visible"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CheckboxButton
                      disabled={!edit}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    >
                      Budget System For Client
                    </CheckboxButton>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="rate_us"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CheckboxButton
                      disabled={!edit}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    >
                      Rate Our Service
                    </CheckboxButton>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="rating_show_in_ticket"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CheckboxButton
                      disabled={!edit}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    >
                      Rating Show in Ticket
                    </CheckboxButton>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {account && (
              <FormField
                control={form.control}
                name="azure_devops_integration"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <CheckboxButton
                        disabled={!edit}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      >
                        Azure Devops Integration
                      </CheckboxButton>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="external_quality_score"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CheckboxButton
                      disabled={!edit}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    >
                      Enternal Quality Score
                    </CheckboxButton>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {account && (
              <FormField
                control={form.control}
                name="ticket_inactivity_notification"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <CheckboxButton
                        disabled={!edit}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      >
                        Ticket Inactivity Notification
                      </CheckboxButton>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>
        {account && form.getValues("ticket_inactivity_notification") && (
          <FormField
            control={form.control}
            name="status_inactivity_threshold"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Inactivity Status and Threshold *</FormLabel>
                <FormControl>
                  <ThresholdList
                    edit={edit}
                    form={form}
                    field={field}
                    thresholds={thresholds}
                    setThresholds={setThresholds}
                    ticket_statuses={account?.ticket_statuses || []}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status*</FormLabel>
              <FormControl>
                <div>
                  <Switch
                    checked={field.value === Constants.ACCOUNT_STATUSES.ACTIVE}
                    onCheckedChange={(checked) =>
                      field.onChange(
                        checked
                          ? Constants.ACCOUNT_STATUSES.ACTIVE
                          : Constants.ACCOUNT_STATUSES.INACTIVE
                      )
                    }
                    disabled={!edit}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-2">
          {mode === "create" && (
            <Link href="/accounts">
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </Link>
          )}
          <Button type="submit" disabled={!edit}>
            Save
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AccountForm;
